import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Users, Settings } from 'lucide-react';
import CreateTerritoriesModal from '../../components/CreateTerritoriesModal';
import ServiceAreasModal from '../../components/resources/ServiceAreasModal';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import { getResources, Resource } from '../../lib/resources';

interface Territory {
  id: string;
  name: string;
  postal_codes: string[];
}

interface ResourceMobileStatus {
  id: string;
  first_name: string;
  last_name: string;
  mobile_available: boolean;
  working_hours?: any;
}

export default function MobileSettings() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [loading, setLoading] = useState(false);
  const [resources, setResources] = useState<ResourceMobileStatus[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [isAreasModalOpen, setIsAreasModalOpen] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string>('monday');
  const [resourceAreas, setResourceAreas] = useState<{[resourceId: string]: {[day: string]: string[]}}>({});
  const { selectedBusiness } = useBusiness();

  const loadTerritories = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_areas')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTerritories(data || []);
    } catch (err) {
      console.error('Error loading territories:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadResourceAreas = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      // Get areas from mobile_availability working_hours field (same as Resources page)
      const { data: mobileData, error: mobileError } = await supabase
        .from('mobile_availability')
        .select('resource_id, working_hours')
        .eq('business_id', selectedBusiness.id);

      if (mobileError) {
        console.error('Error loading mobile availability areas:', mobileError);
        return;
      }

      // Extract areas from working_hours JSON field
      const areasMap: {[resourceId: string]: {[day: string]: string[]}} = {};
      mobileData?.forEach(mobile => {
        if (mobile.working_hours) {
          areasMap[mobile.resource_id] = {};
          
          // Extract areas for each day from working_hours
          ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
            const dayData = mobile.working_hours[day];
            if (dayData && dayData.areas) {
              areasMap[mobile.resource_id][day] = dayData.areas;
            } else {
              areasMap[mobile.resource_id][day] = [];
            }
          });
        }
      });

      setResourceAreas(areasMap);
    } catch (err) {
      console.error('Error loading resource areas:', err);
    }
  };

  const loadResources = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      setResourcesLoading(true);
      
      // Get all resources for this business
      const allResources = await getResources(selectedBusiness.id);
      
      // Filter to only show active resources
      const activeResources = allResources.filter(resource => resource.status === 'active');
      
      // Get mobile availability for all resources
      const { data: mobileData, error: mobileError } = await supabase
        .from('mobile_availability')
        .select('resource_id, mobile_available, working_hours')
        .eq('business_id', selectedBusiness.id);

      if (mobileError) {
        console.error('Error loading mobile availability:', mobileError);
      }

      // Combine resource data with mobile availability (only active resources)
      const resourcesWithMobileStatus: ResourceMobileStatus[] = activeResources.map(resource => {
        const mobileInfo = mobileData?.find(m => m.resource_id === resource.id);
        return {
          id: resource.id,
          first_name: resource.first_name,
          last_name: resource.last_name,
          mobile_available: !!mobileInfo, // True if record exists in mobile_availability
          working_hours: mobileInfo?.working_hours
        };
      });

      setResources(resourcesWithMobileStatus);
      
      // Also load resource areas
      await loadResourceAreas();
    } catch (err) {
      console.error('Error loading resources:', err);
    } finally {
      setResourcesLoading(false);
    }
  };

  useEffect(() => {
    loadTerritories();
    loadResources();
  }, [selectedBusiness?.id]);

  // Refresh territories when modal is closed
  const handleCloseModal = () => {
    setIsModalOpen(false);
    loadTerritories();
  };

  const handleMobileToggle = async (resourceId: string, currentStatus: boolean) => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      const newStatus = !currentStatus;
      
      if (newStatus) {
        // Enable mobile availability - create record
        const { data: resourceData, error: resourceError } = await supabase
          .from('resources')
          .select('first_name, last_name')
          .eq('id', resourceId)
          .single();

        if (resourceError) throw resourceError;

        const { error: insertError } = await supabase
          .from('mobile_availability')
          .insert({
            resource_id: resourceId,
            business_id: selectedBusiness.id,
            mobile_available: true,
            working_hours: {
              monday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
              tuesday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
              wednesday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
              thursday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
              friday: { enabled: true, shifts: [{ start: '09:00', end: '17:00' }] },
              saturday: { enabled: false, shifts: [] },
              sunday: { enabled: false, shifts: [] }
            }
          });

        if (insertError) throw insertError;
      } else {
        // Disable mobile availability - delete record
        const { error: deleteError } = await supabase
          .from('mobile_availability')
          .delete()
          .eq('resource_id', resourceId);

        if (deleteError) throw deleteError;
      }

      // Reload resources to reflect changes
      await loadResources();
    } catch (err) {
      console.error('Error toggling mobile availability:', err);
    }
  };

  const handleAreasClick = (resourceId: string, day: string = 'monday') => {
    setSelectedResourceId(resourceId);
    setSelectedDay(day);
    setIsAreasModalOpen(true);
  };

  const handleAreasSave = async (selectedAreas: string[]) => {
    if (!selectedResourceId || !selectedBusiness?.id || !supabase) return;

    try {
      // Get current mobile availability data
      const { data: currentData, error: fetchError } = await supabase
        .from('mobile_availability')
        .select('working_hours')
        .eq('resource_id', selectedResourceId)
        .single();

      if (fetchError) {
        console.error('Error fetching current mobile availability:', fetchError);
        return;
      }

      // Update the working_hours for the selected day
      const updatedWorkingHours = {
        ...currentData.working_hours,
        [selectedDay]: {
          ...currentData.working_hours[selectedDay],
          areas: selectedAreas
        }
      };

      // Update the mobile_availability record
      const { error: updateError } = await supabase
        .from('mobile_availability')
        .update({ working_hours: updatedWorkingHours })
        .eq('resource_id', selectedResourceId);

      if (updateError) {
        console.error('Error updating mobile availability:', updateError);
        return;
      }

      // Reload resource areas to reflect changes
      await loadResourceAreas();
    } catch (err) {
      console.error('Error saving resource areas:', err);
    }
  };

  const getResourceAreasCount = (resourceId: string): number => {
    const areas = resourceAreas[resourceId];
    if (!areas) return 0;
    
    // Count unique areas across all days
    const allAreas = new Set<string>();
    Object.values(areas).forEach(dayAreas => {
      dayAreas.forEach(areaId => allAreas.add(areaId));
    });
    
    return allAreas.size;
  };

  return (
    <div className="min-h-screen bg-gray-100 relative z-0" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'}}>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12 relative z-0">
        {/* Floating Container */}
        <div className="bg-gray-50 rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-8 lg:p-16 mb-8 sm:mb-12 lg:mb-20 relative z-0">


          {/* Service Territories Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
            {/* Card Header - Stack on mobile, side by side on larger screens */}
            <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-6">
              <div className="text-center sm:text-left">
                <h2 className="text-xl sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">Service Territories</h2>
                <p className="text-sm sm:text-base text-gray-600">Define your mobile service coverage areas</p>
              </div>
              
              {/* Button - Full width on mobile, auto on larger screens */}
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 sm:px-6 sm:py-3 lg:px-8 lg:py-4 text-base sm:text-sm lg:text-base font-semibold text-white bg-purple-700 rounded-2xl hover:bg-purple-600 transition-all duration-200 active:scale-95 shadow-lg"
              >
                <Plus className="h-5 w-5 sm:h-4 sm:w-4 lg:h-5 lg:w-5 mr-2" />
                <span className="sm:hidden">Add Territory</span>
                <span className="hidden sm:inline">Manage Territories</span>
              </button>
            </div>
            
            {/* Content Area */}
            <div>
              {loading ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
                  </div>
                  <p className="text-sm sm:text-base text-gray-500 mt-4">Loading territories...</p>
                </div>
              ) : territories.length === 0 ? (
                <div className="text-center py-12 sm:py-16 px-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-2xl bg-gray-100 flex items-center justify-center shadow-sm border border-gray-200">
                    <MapPin className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    No Service Territories
                  </h3>
                  <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-sm mx-auto leading-relaxed">
                    Add service territories to define your mobile service coverage areas.
                  </p>
                  
                  {/* Mobile CTA Button in empty state */}
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="mt-6 sm:hidden w-full inline-flex items-center justify-center px-6 py-3 text-base font-semibold text-white bg-purple-700 rounded-2xl hover:bg-purple-600 transition-all duration-200 active:scale-95 shadow-lg"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Your First Territory
                  </button>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                  {territories.map((territory) => (
                    <div 
                      key={territory.id} 
                      className="bg-gray-50 rounded-2xl p-4 sm:p-5 lg:p-6 border border-gray-200 transition-all duration-200 hover:bg-gray-100 active:scale-95 shadow-sm"
                    >
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">{territory.name}</h4>
                      
                      {/* Postal codes with improved mobile layout */}
                      <div className="flex flex-wrap gap-2 sm:gap-2.5">
                        {territory.postal_codes.map((code, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1.5 sm:px-3 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold bg-white text-gray-700 border border-gray-200 shadow-sm"
                          >
                            {code}
                          </span>
                        ))}
                      </div>
                      
                      {/* Show postal code count on mobile if many codes */}
                      {territory.postal_codes.length > 6 && (
                        <p className="text-xs text-gray-500 mt-3 sm:hidden">
                          {territory.postal_codes.length} postal codes
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Resources Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8">
            {/* Card Header */}
            <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-6">
              <div className="text-center sm:text-left">
                <h2 className="text-xl sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2">Mobile Resources</h2>
                <p className="text-sm sm:text-base text-gray-600">Manage which team members provide mobile services</p>
              </div>
            </div>
            
            {/* Content Area */}
            <div>
              {resourcesLoading ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mx-auto"></div>
                  </div>
                  <p className="text-sm sm:text-base text-gray-500 mt-4">Loading resources...</p>
                </div>
              ) : resources.length === 0 ? (
                <div className="text-center py-12 sm:py-16 px-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-2xl bg-gray-100 flex items-center justify-center shadow-sm border border-gray-200">
                    <Users className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
                  </div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    No Team Members
                  </h3>
                  <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-sm mx-auto leading-relaxed">
                    Add team members to manage their mobile service availability.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                  {resources.map((resource) => (
                    <div 
                      key={resource.id} 
                      className="bg-gray-50 rounded-2xl border border-gray-200 transition-all duration-200 hover:bg-gray-100 shadow-sm"
                    >
                      {/* Resource Header with Toggle */}
                      <div className="p-4 sm:p-5 lg:p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center text-sm font-bold text-white shadow-sm">
                              {resource.first_name[0]}{resource.last_name[0]}
                            </div>
                            <div>
                              <h4 className="text-base sm:text-lg font-bold text-gray-900">
                                {resource.first_name} {resource.last_name}
                              </h4>
                              <p className="text-xs sm:text-sm text-gray-600">
                                Mobile Service {resource.mobile_available ? 'Enabled' : 'Disabled'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Toggle Switch */}
                          <div className="flex items-center space-x-3">
                            <span className="text-sm text-gray-600">
                              {resource.mobile_available ? 'Enabled' : 'Disabled'}
                            </span>
                            <button
                              onClick={() => handleMobileToggle(resource.id, resource.mobile_available)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                                resource.mobile_available ? 'bg-purple-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                  resource.mobile_available ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Working Hours Section - Only show if mobile is enabled */}
                      {resource.mobile_available && resource.working_hours && (
                        <div className="border-t border-gray-200 bg-white rounded-b-2xl">
                          <div className="p-4 sm:p-5 lg:p-6">
                            <h5 className="text-sm font-semibold text-gray-900 mb-4">Working Hours</h5>
                            <div className="space-y-2">
                              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                                const dayData = resource.working_hours[day];
                                if (!dayData) return null;
                                
                                const dayAreas = resourceAreas[resource.id]?.[day] || [];
                                
                                return (
                                  <div key={day} className="flex items-center justify-between py-2">
                                    <span className="text-sm font-medium text-gray-700 capitalize">
                                      {day}
                                    </span>
                                    <div className="flex items-center space-x-2">
                                      <span className={`text-sm ${dayData.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                                        {dayData.enabled && dayData.shifts?.length > 0 
                                          ? `Available ${dayData.shifts[0].start} - ${dayData.shifts[0].end}`
                                          : 'Not available'
                                        }
                                      </span>
                                      {/* Areas button for each day - only show if day is enabled */}
                                      {dayData.enabled && (
                                        <button
                                          onClick={() => handleAreasClick(resource.id, day)}
                                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                                        >
                                          Areas {dayAreas.length}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <CreateTerritoriesModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {selectedResourceId && selectedBusiness?.id && (
        <ServiceAreasModal
          isOpen={isAreasModalOpen}
          onClose={() => {
            setIsAreasModalOpen(false);
            setSelectedResourceId(null);
          }}
          businessId={selectedBusiness.id}
          day={selectedDay}
          onSave={handleAreasSave}
          currentAreas={resourceAreas[selectedResourceId]?.[selectedDay] || []}
        />
      )}
    </div>
  );
}